"use client";

import { motion, type MotionValue, useTransform } from "motion/react";
import { useMemo } from "react";
import {
  AXIS_LABEL_BUDGET_CH,
  type AxisLabelBudget,
} from "../axis-label-budget";
import { chartCssVars } from "../chart-context";
import { type ChartProjection, useChartProjection } from "./chart-projection-context";
import { projectView } from "./iso-projection";
import { useProjectionProgress } from "./use-projected-path";

export type ProjectedAxis = "x" | "y" | "z";

export interface ProjectedAxisLabel {
  key: string;
  text: string;
  x: number;
  y: number;
  z: number;
}

export interface ProjectedAxisLabelsProps {
  labels: ReadonlyArray<ProjectedAxisLabel>;
  axis: ProjectedAxis;
  budget?: AxisLabelBudget;
  fontSize?: number;
  textAnchor?: "start" | "middle" | "end";
  dx?: number;
  dy?: number;
  fill?: string;
  className?: string;
}

const CHAR_WIDTH_EM = 0.6;
const LABEL_GAP_PX = 6;

const AXIS_DEFAULTS: Record<
  ProjectedAxis,
  { textAnchor: "start" | "middle" | "end"; dx: number; dy: number }
> = {
  x: { textAnchor: "middle", dx: 0, dy: 14 },
  y: { textAnchor: "end", dx: -6, dy: 4 },
  z: { textAnchor: "end", dx: -6, dy: 4 },
};

export const truncateLabel = (text: string, maxChars: number): string =>
  text.length > maxChars ? `${text.slice(0, Math.max(1, maxChars - 1))}…` : text;

export function thinProjectedLabels(
  points: ReadonlyArray<{ sx: number; sy: number; chars: number }>,
  axis: ProjectedAxis,
  fontSize: number
): boolean[] {
  const keep = points.map(() => false);
  let lastIndex = -1;
  for (let index = 0; index < points.length; index++) {
    const point = points[index];
    if (lastIndex < 0) {
      keep[index] = true;
      lastIndex = index;
      continue;
    }
    const last = points[lastIndex];
    const needed =
      axis === "x"
        ? ((last.chars + point.chars) / 2) * fontSize * CHAR_WIDTH_EM + LABEL_GAP_PX
        : fontSize + 2;
    const distance =
      axis === "x" ? Math.abs(point.sx - last.sx) : Math.abs(point.sy - last.sy);
    if (distance >= needed) {
      keep[index] = true;
      lastIndex = index;
    }
  }
  return keep;
}

function ProjectedLabelText({
  projection,
  label,
  e,
  opacity,
  textAnchor,
  dx,
  dy,
  fontSize,
  fill,
}: {
  projection: ChartProjection;
  label: ProjectedAxisLabel;
  e: MotionValue<number>;
  opacity: MotionValue<number>;
  textAnchor: "start" | "middle" | "end";
  dx: number;
  dy: number;
  fontSize: number;
  fill: string;
}) {
  const x = useTransform(e, (v) => projection.project(label.x, label.y, label.z, v)[0] + dx);
  const y = useTransform(e, (v) => projection.project(label.x, label.y, label.z, v)[1] + dy);
  return (
    <motion.text
      x={x}
      y={y}
      textAnchor={textAnchor}
      fontSize={fontSize}
      fill={fill}
      style={{ opacity }}
      className="tabular-nums"
    >
      {label.text}
    </motion.text>
  );
}

export function ProjectedAxisLabels({
  labels,
  axis,
  budget = axis === "z" ? "yAxisTick" : axis === "y" ? "yAxisCategory" : "xAxisTick",
  fontSize = 11,
  textAnchor,
  dx,
  dy,
  fill = chartCssVars.label,
  className,
}: ProjectedAxisLabelsProps) {
  const projection = useChartProjection();
  const e = useProjectionProgress(projection);
  const opacity = useTransform(e, [0.6, 1], [0, 1]);
  const defaults = AXIS_DEFAULTS[axis];
  const maxChars = AXIS_LABEL_BUDGET_CH[budget];

  const visible = useMemo(() => {
    if (!projection) return [];
    const view = projection.at(1);
    const prepared = labels.map((label) => ({
      ...label,
      text: truncateLabel(label.text, maxChars),
    }));
    const keep = thinProjectedLabels(
      prepared.map((label) => {
        const [sx, sy] = projectView(view, label.x, label.y, label.z);
        return { sx, sy, chars: label.text.length };
      }),
      axis,
      fontSize
    );
    return prepared.filter((_, index) => keep[index]);
  }, [projection, labels, maxChars, axis, fontSize]);

  if (!projection) {
    return null;
  }

  return (
    <g aria-hidden pointerEvents="none" className={className}>
      {visible.map((label) => (
        <ProjectedLabelText
          key={label.key}
          projection={projection}
          label={label}
          e={e}
          opacity={opacity}
          textAnchor={textAnchor ?? defaults.textAnchor}
          dx={dx ?? defaults.dx}
          dy={dy ?? defaults.dy}
          fontSize={fontSize}
          fill={fill}
        />
      ))}
    </g>
  );
}
